import { useEffect, useState } from 'react';
import type { ServiceProvider } from '@shopgnd/shared';
import { getServiceProvider, getServiceProviderForOwnerUser, getVendor } from '@/api/catalog';
import { canAdminImpersonate } from '@/lib/adminImpersonation';
import { useAdminImpersonationStore } from '@/stores/adminImpersonationStore';
import { useAuthStore } from '@/stores/authStore';

export function useProProfile(): { pro: ServiceProvider | null; ready: boolean } {
  const user = useAuthStore((s) => s.user);
  const impersonation = useAdminImpersonationStore((s) => s.session);
  const [pro, setPro] = useState<ServiceProvider | null>(null);
  const impersonatingPro = impersonation?.kind === 'pro' && canAdminImpersonate(user);
  const impersonatingVendor =
    impersonation?.kind === 'vendor' && canAdminImpersonate(user);
  const [ready, setReady] = useState(() => !user && !impersonatingPro && !impersonatingVendor);

  useEffect(() => {
    if (impersonatingPro && impersonation?.kind === 'pro') {
      let cancelled = false;
      setReady(false);
      void getServiceProvider(impersonation.proId)
        .then((p) => {
          if (cancelled) return;
          setPro(p ?? null);
          setReady(true);
        })
        .catch(() => {
          if (cancelled) return;
          setPro(null);
          setReady(true);
        });
      return () => {
        cancelled = true;
      };
    }

    if (impersonatingVendor && impersonation?.kind === 'vendor') {
      let cancelled = false;
      setReady(false);
      void getVendor(impersonation.vendorId)
        .then((v) => {
          if (cancelled) return;
          const linkedId = v?.linkedProId;
          if (!linkedId) {
            setPro(null);
            setReady(true);
            return;
          }
          void getServiceProvider(linkedId)
            .then((p) => {
              if (cancelled) return;
              setPro(p ?? null);
              setReady(true);
            })
            .catch(() => {
              if (cancelled) return;
              setPro(null);
              setReady(true);
            });
        })
        .catch(() => {
          if (cancelled) return;
          setPro(null);
          setReady(true);
        });
      return () => {
        cancelled = true;
      };
    }

    if (!user) {
      setPro(null);
      setReady(true);
      return;
    }
    let cancelled = false;
    setReady(false);
    void getServiceProviderForOwnerUser(user.id)
      .then((owned) => {
        if (cancelled) return;
        setPro(owned ?? null);
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setPro(null);
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user, impersonation, impersonatingPro, impersonatingVendor]);

  return { pro, ready };
}

export function useProForUser(): ServiceProvider | null {
  return useProProfile().pro;
}
